/**
 * Utility which helps construct URLs to image resources hosted using the Amplience Dynamic Media platform.
 *
 * Commonly used transformations can chained together by calling the appropriate function on the builder.
 * Advanced transformations can be used by configuring a transformation template within the Amplience
 * back-office and applying the template to the builder.
 */
export class ImageUrlBuilder {
    constructor(media) {
        this.media = media;
        this._protocol = 'https';
        this._query = [];
    }
    /**
     * Protocol configures what protocol style to use when building the image URL.
     *
     * ```{{protocol}}[:]//images.site.com/i/account/image```
     * @param value URL protocol, valid values are: http, https and // for protocol relative URLs
     */
    protocol(value) {
        this._protocol = value;
        return this;
    }
    /**
     * Host configures the hostname to use when building the image URL.
     * By default, the SDK will use the most appropriate hostname based on
     * the SDK configuration.
     *
     * ```https://{{host}}/i/account/image```
     * @param value Hostname to use instead of the default value.
     */
    host(value) {
        this._host = value;
        return this;
    }
    /**
     * Format converts the image to the file format specified.
     * The format will be appended to the URL as a file extension.
     *
     * ```https://images.site.com/i/account/image.webp```
     * @param value  Images format to use when encoding the output.
     */
    format(value) {
        this._format = value;
        return this;
    }
    /**
     * SEO filename allows a custom filename to be used to improve SEO.
     *
     * ```https://images.site.com/i/account/image/{{seoFileName}}```
     * @param value
     */
    seoFileName(value) {
        this._seoFileName = value;
        return this;
    }
    /**
     * Template applies a transformation template to the image, which will
     * apply all of the transformation parameters contained in the template to the image.
     *
     * ```https://images.site.com/i/account/image?${{name}}$```
     * @param name The name of the transformation template previously configured in the Amplience back-office.
     */
    template(name) {
        this._query.push(`$${encodeURIComponent(name)}$`);
        return this;
    }
    /**
     * Parameter appends the specified parameter to the query string. This can be used to
     * pass variables into templates.
     *
     * ```https://images.site.com/i/account/image?{{name}}={{value}}```
     * @param name
     * @param value
     */
    parameter(name, value) {
        this._query.push(`${encodeURIComponent(name)}=${encodeURIComponent(value)}`);
        return this;
    }
    /**
     * Quality sets the compression level for the image output
     *
     * ```https://images.site.com/i/account/image?qlt={{value}}```
     * @param value The quality percentage (0-100)
     */
    quality(value) {
        this._query.push(`qlt=${value}`);
        return this;
    }
    /**
     * Sharpen applies an unsharp mask to the image to refine edges or make an image look more crisp.
     *
     * ```https://images.site.com/i/account/image?unsharp={{radius}},{{sigma}},{{amount}},{{threshold}}```
     *
     * @param radius The radius of the Gaussian, in pixels, not counting the center pixel.
     * @param sigma The standard deviation of the Gaussian, in pixels.
     * @param amount The percentage of the difference between the original and the blur image that is added back into the original.
     * @param threshold The threshold, as a fraction of MaxRGB, needed to apply the difference amount.
     */
    sharpen(radius = 0, sigma = 1, amount = 1, threshold = 0.05) {
        this._query.push(`unsharp=${radius},${sigma},${amount},${threshold}`);
        return this;
    }
    /**
     * Width resizes an image to the pixel size provided. If you only provide one axis,
     * the other will be calculated to maintain aspect ratio. Use the scale mode parameter
     * to apply different resize effects like stretch or crop.
     *
     * ```https://images.site.com/i/account/image?w={{value}}```
     * @param value The new width in pixels.
     */
    width(value) {
        this._query.push(`w=${value}`);
        return this;
    }
    /**
     * Height resizes an image to the pixel size provided. If you only provide one axis,
     * the other will be calculated to maintain aspect ratio. Use the scale mode parameter
     * to apply different resize effects like stretch or crop.
     *
     * ```https://images.site.com/i/account/image?h={{value}}```
     * @param value The new height in pixels.
     */
    height(value) {
        this._query.push(`h=${value}`);
        return this;
    }
    /**
     * Returns the fully constructed URL for this image with any transformations.
     */
    build() {
        const isSecure = this._protocol === '//' || this._protocol === 'https';
        const isVideo = this.media._meta &&
            this.media._meta.schema ===
                'http://bigcontent.io/cms/schema/v1/core#/definitions/video-link';
        let url = this._protocol === '//' ? '//' : `${this._protocol}://`;
        const host = this._host ? this._host : this.media.getHost(isSecure);
        url += host;
        url += `/${isVideo ? 'v' : 'i'}/`;
        url += encodeURIComponent(this.media.endpoint);
        url += '/';
        url += encodeURIComponent(this.media.name);
        if (this._seoFileName) {
            url += '/';
            url += encodeURIComponent(this._seoFileName);
        }
        if (this._format) {
            url += `.${this._format}`;
        }
        if (this._query.length > 0) {
            url += '?';
            url += this._query.join('&');
        }
        return url;
    }
}
//# sourceMappingURL=data:application/json;base64,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